import { useEffect } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
    RoiPropertiesUpdatedEvent,
    StructureSetApprovalChangeRequestedEvent,
    StructureSetContourThicknessChangeRequestedEvent,
    StructureSetDescriptionChangeRequestedEvent,
    StructureSetLabelChangeRequestedEvent,
    StructureSetVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { getStructureSetRepository } from '~/domain/ServiceContainer';
import {
    currentStructureSetIdState,
    currentStructureSetState,
} from '~/state/atoms/structureSet/structureSetAtoms';

/**
 * Application service layer for {@link StructureSet} operations.
 *
 * Processes structure set domain events by coordinating between domain models,
 * state management, and external systems.
 */
export function useStructureSetHandlers() {
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const handleStructureSetApprovalChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetApprovalChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for approval change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const repository = getStructureSetRepository();

                    const updatedReadModel = repository.updateStructureSet(
                        currentStructureSetId,
                        (structureSet) => {
                            structureSet.setApprovalStatus(
                                event.newApprovalStatus,
                                event.reviewerName,
                                event.source === 'user' // Only cascade for user events
                            );
                        }
                    );

                    set(currentStructureSetState, updatedReadModel);

                    // Emit individual ROI property updated events for each ROI
                    // only when cascading occurred (user events)
                    if (event.source === 'user') {
                        const roiUpdatePromises = updatedReadModel.roiArray.map(
                            (roi) =>
                                domainEventBus.publish(
                                    new RoiPropertiesUpdatedEvent(
                                        roi.roiNumber,
                                        {
                                            approvalStatus:
                                                event.newApprovalStatus,
                                        },
                                        'system'
                                    )
                                )
                        );

                        await Promise.all(roiUpdatePromises);
                    }
                } catch (error) {
                    console.error(
                        `Failed to handle structure set approval change: ${error}`
                    );
                }
            },
        [currentStructureSetId]
    );

    const handleStructureSetLabelChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetLabelChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for label change'
                        );
                        return;
                    }
                    const { set } = recoilInterface;
                    const repository = getStructureSetRepository();

                    const updatedReadModel = repository.updateStructureSet(
                        currentStructureSetId,
                        (structureSet) => {
                            structureSet.structureSetLabel = event.newLabel;
                        }
                    );

                    set(currentStructureSetState, updatedReadModel);
                } catch (error) {
                    console.error(
                        'Failed to update structure set label:',
                        error
                    );
                    throw error;
                }
            },
        [currentStructureSetId]
    );

    const handleStructureSetDescriptionChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetDescriptionChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for description change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const repository = getStructureSetRepository();

                    const updatedReadModel = repository.updateStructureSet(
                        currentStructureSetId,
                        (structureSet) => {
                            structureSet.structureSetDescription =
                                event.newDescription;
                        }
                    );

                    set(currentStructureSetState, updatedReadModel);
                } catch (error) {
                    console.error(
                        'Failed to update structure set description:',
                        error
                    );
                    throw error;
                }
            },
        [currentStructureSetId]
    );

    const handleStructureSetVisibilityChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetVisibilityChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for visibility change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const repository = getStructureSetRepository();

                    const updatedReadModel = repository.updateStructureSet(
                        currentStructureSetId,
                        (structureSet) => {
                            structureSet.roiArray.forEach((roi) => {
                                roi.visible = event.visible;
                            });
                        }
                    );

                    set(currentStructureSetState, updatedReadModel);

                    // Emit individual ROI property updated events for each ROI
                    const roiUpdatePromises = updatedReadModel.roiArray.map(
                        (roi) =>
                            domainEventBus.publish(
                                new RoiPropertiesUpdatedEvent(
                                    roi.roiNumber,
                                    { visible: event.visible },
                                    event.source
                                )
                            )
                    );

                    await Promise.all(roiUpdatePromises);
                } catch (error) {
                    console.error(
                        `Failed to handle structure set visibility change: ${error}`
                    );
                }
            },
        [currentStructureSetId]
    );

    const handleStructureSetContourThicknessChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetContourThicknessChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for contour thickness change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const repository = getStructureSetRepository();

                    const updatedReadModel = repository.updateStructureSet(
                        currentStructureSetId,
                        (structureSet) => {
                            structureSet.roiArray.forEach((roi) => {
                                roi.contourThickness = event.contourThickness;
                            });
                        }
                    );

                    set(currentStructureSetState, updatedReadModel);

                    // Emit individual ROI property updated events for each ROI
                    const roiUpdatePromises = updatedReadModel.roiArray.map(
                        (roi) =>
                            domainEventBus.publish(
                                new RoiPropertiesUpdatedEvent(
                                    roi.roiNumber,
                                    {
                                        contourThickness:
                                            event.contourThickness,
                                    },
                                    event.source
                                )
                            )
                    );

                    await Promise.all(roiUpdatePromises);
                } catch (error) {
                    console.error(
                        `Failed to handle structure set contour thickness change: ${error}`
                    );
                }
            },
        [currentStructureSetId]
    );

    const handleStructureSetUndoOrRedoRequest = useRecoilCallback(
        (recoilInterface) =>
            (event: ContourUndoRequestedEvent | ContourRedoRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn('No structure set open');
                        return;
                    }

                    const { set } = recoilInterface;
                    const repository = getStructureSetRepository();

                    const updatedReadModel = repository.updateRoi(
                        currentStructureSetId,
                        event.roiNumber,
                        () => {
                            /* NOP */
                        }
                    );

                    set(currentStructureSetState, updatedReadModel);
                } catch (error) {
                    console.error(
                        `Failed to handle structure set visibility change: ${error}`
                    );
                }
            },
        [currentStructureSetId]
    );

    useEffect(() => {
        const subscriptions = [
            domainEventBus.subscribe(
                StructureSetApprovalChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetApprovalChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetLabelChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetLabelChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetDescriptionChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetDescriptionChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetVisibilityChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetVisibilityChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetContourThicknessChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetContourThicknessChangeRequest
            ),
            domainEventBus.subscribe(
                ContourRedoRequestedEvent.EVENT_TYPE,
                handleStructureSetUndoOrRedoRequest
            ),
            domainEventBus.subscribe(
                ContourUndoRequestedEvent.EVENT_TYPE,
                handleStructureSetUndoOrRedoRequest
            ),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, [
        handleStructureSetApprovalChangeRequest,
        handleStructureSetLabelChangeRequest,
        handleStructureSetDescriptionChangeRequest,
        handleStructureSetVisibilityChangeRequest,
        handleStructureSetContourThicknessChangeRequest,
        handleStructureSetUndoOrRedoRequest,
    ]);
}
